import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useCreateSop, useSopVideos } from '../hooks.ts';
import VideoPicker from '../components/VideoPicker.tsx';

const MAX_TITLE = 255;
const MAX_DESCRIPTION = 4000;
const MAX_INSTRUCTIONS = 2000;

export default function SopNewPage() {
  const navigate = useNavigate();
  const { data: videos = [], isLoading, error } = useSopVideos();
  const create = useCreateSop();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [videoId, setVideoId] = useState('');
  const [generate, setGenerate] = useState(true);
  const [instructions, setInstructions] = useState('');
  const [touched, setTouched] = useState(false);

  const readyCount = videos.filter((v) => v.media_status === 'ready').length;
  // If the chosen video stops being usable (e.g. it expired while the form was open), drop the selection.
  useEffect(() => {
    if (videoId && !videos.some((v) => v.id === videoId && v.media_status === 'ready')) setVideoId('');
  }, [videos, videoId]);

  const titleError = touched && !title.trim() ? 'Enter a title' : '';
  const videoError = touched && !videoId ? 'Choose a video that is ready' : '';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!title.trim() || !videoId) return;
    create.mutate(
      {
        video_id: videoId,
        title: title.trim(),
        description: description.trim() || undefined,
        instructions: generate ? instructions.trim() || undefined : undefined,
        generate,
      },
      { onSuccess: ({ sop }) => navigate(`/sop-synthesizer/${sop.id}`) },
    );
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
      <Link to="/sop-synthesizer" className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to SOPs
      </Link>
      <h1 className="mt-3 text-2xl font-bold text-gray-900">New SOP</h1>
      <p className="mb-6 text-sm text-gray-500">Give it a name, then pick the walkthrough video it will be written from.</p>

      <form onSubmit={submit} className="space-y-5 rounded-lg border border-gray-200 bg-white p-6" noValidate>
        <div>
          <label htmlFor="sop-title" className="block text-sm font-medium text-gray-700">Title <span aria-hidden="true">*</span></label>
          <input
            id="sop-title"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={title}
            maxLength={MAX_TITLE}
            onChange={(e) => setTitle(e.target.value)}
            aria-invalid={!!titleError}
            placeholder="e.g. Month-end bank reconciliation"
          />
          {titleError && <p className="mt-1 text-xs text-red-600">{titleError}</p>}
        </div>

        <div>
          <label htmlFor="sop-desc" className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            id="sop-desc"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            rows={3}
            value={description}
            maxLength={MAX_DESCRIPTION}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this SOP covers and who it is for"
          />
        </div>

        <div>
          <span className="block text-sm font-medium text-gray-700">Source video <span aria-hidden="true">*</span></span>
          <div className="mt-1">
            {isLoading ? (
              <p className="text-sm text-gray-500">Loading videos…</p>
            ) : error ? (
              <p className="text-sm text-red-600">Could not load videos.</p>
            ) : videos.length === 0 ? (
              <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                Your organization has no videos yet. Your administrator uploads walkthrough videos and prepares them.
              </p>
            ) : (
              <>
                <VideoPicker videos={videos} value={videoId} onChange={setVideoId} />
                {readyCount === 0 && (
                  <p className="mt-2 text-xs text-amber-700">None of your videos are ready yet. Once media processing finishes you can pick one here.</p>
                )}
              </>
            )}
            {videoError && <p className="mt-1 text-xs text-red-600">{videoError}</p>}
          </div>
        </div>

        <div className="rounded-md bg-gray-50 p-3">
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input type="checkbox" checked={generate} onChange={(e) => setGenerate(e.target.checked)} />
            Generate the draft as soon as I create it
          </label>
          <p className="mt-1 text-xs text-gray-500">
            {generate
              ? 'The draft is written in the background and can take several minutes. You can leave the page; its progress shows in the SOP list.'
              : 'You will get an empty draft. Generate it later from the SOP page.'}
          </p>
          {generate && (
            <div className="mt-3">
              <label htmlFor="sop-instructions" className="block text-sm font-medium text-gray-700">Guidance for the draft (optional)</label>
              <textarea
                id="sop-instructions"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                rows={2}
                value={instructions}
                maxLength={MAX_INSTRUCTIONS}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="e.g. Focus on the month-end steps and ignore the introduction."
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Link to="/sop-synthesizer" className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-md bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {create.isPending ? 'Creating…' : 'Create SOP'}
          </button>
        </div>
      </form>
    </div>
  );
}
